
import { useState, useEffect } from "react";
import { NotificationSettings } from "./NotificationSettings";
import { getAllCountries } from "@/lib/phoneUtils";
import { Button } from "@/components/ui/button";
import { PwaDebugModal } from "@/components/PwaDebugModal";
import { usePwaInstall } from "@/hooks/usePwaInstall";
import { useIsMobile } from "@/hooks/use-mobile";
import * as UAParserLib from 'ua-parser-js';
import { Smartphone, Download, MoveDown, Bell, ArrowDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from "@/hooks/useAuth";

interface SettingsNotificationsTabProps {
  showOnlyInstallSection?: boolean;
  showOnlyNotificationSettings?: boolean;
}

export function SettingsNotificationsTab({ 
  showOnlyInstallSection = false, 
  showOnlyNotificationSettings = false 
}: SettingsNotificationsTabProps) {
  const [debugModalOpen, setDebugModalOpen] = useState(false);
  const { isInstallable, isIOS, promptToInstall, resetInstallDismissed } = usePwaInstall();
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isStandalone, setIsStandalone] = useState(false);
  const [notificationsSupported, setNotificationsSupported] = useState(true);
  const [showIosInstructions, setShowIosInstructions] = useState(false);
  const [debugInfo, setDebugInfo] = useState({
    isIOS: false,
    isMobile: false,
    isInstallable: false,
    userAgent: '',
    osInfo: {
      name: undefined,
      version: undefined
    },
    browserInfo: {
      name: undefined,
      version: undefined
    },
    inStandaloneMode: false,
    storedDismissed: false,
    maxTouchPoints: 0
  });
  
  useEffect(() => {
    const countries = getAllCountries();
    console.log("Available countries:", countries);
    
    setIsStandalone(window.matchMedia('(display-mode: standalone)').matches);
    
    setNotificationsSupported(typeof window !== 'undefined' && 'Notification' in window);
    
    if (typeof window !== 'undefined') {
      const parser = new UAParserLib.UAParser();
      const result = parser.getResult();
      
      setDebugInfo({
        isIOS,
        isMobile,
        isInstallable,
        userAgent: navigator.userAgent,
        osInfo: {
          name: result.os.name,
          version: result.os.version
        },
        browserInfo: {
          name: result.browser.name,
          version: result.browser.version
        },
        inStandaloneMode: window.matchMedia('(display-mode: standalone)').matches,
        storedDismissed: localStorage.getItem('pwa-install-dismissed') === 'true',
        maxTouchPoints: navigator.maxTouchPoints || 0
      });
    }
  }, [isIOS, isMobile, isInstallable]);

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIosInstructions(true);
    } else {
      try {
        await promptToInstall();
      } catch (error) {
        console.error('Install error:', error);
        toast({
          title: t('pwa.installError', 'Installation error'),
          description: t('pwa.tryAgain', 'Please try again later'),
          variant: 'destructive',
          duration: 3000
        });
      }
    }
  };

  const renderInstallSection = () => {
    // Don't show install section if user is not logged in or if app is already in standalone mode
    if (isStandalone || !user) {
      return null;
    }
    
    return (
      <div className="border rounded-lg overflow-hidden">
        <div className="bg-muted/30 p-4 border-b">
          <h3 className="text-lg font-semibold">App Installation</h3>
          <p className="text-sm text-muted-foreground">
            Install mishu for faster access and instant notifications
          </p>
        </div>
        
        <div className="p-4">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Smartphone className="h-5 w-5 mr-3 text-primary" />
                <div>
                  <p className="font-medium">Install mishu</p>
                  <p className="text-sm text-muted-foreground">Get instant notifications and one-tap access from your home screen</p>
                </div>
              </div>
              <Button 
                variant="default"
                className="bg-primary hover:bg-primary/90"
                onClick={handleInstallClick}
                disabled={!isInstallable && !debugInfo.storedDismissed}
              >
                <MoveDown className="mr-2 h-4 w-4" />
                Install
              </Button>
            </div>
            
            <div className="rounded-lg p-3 bg-muted/30 space-y-2">
              <div className="flex items-center">
                <Bell className="h-4 w-4 mr-2 text-primary" />
                <p className="text-sm">Receive immediate notifications even when the browser is closed</p>
              </div>
              <div className="flex items-center">
                <Smartphone className="h-4 w-4 mr-2 text-primary" />
                <p className="text-sm">Quick access from your home screen without typing URLs</p>
              </div>
              <div className="flex items-center">
                <Download className="h-4 w-4 mr-2 text-primary" />
                <p className="text-sm">Works offline and loads instantly with cached data</p>
              </div>
            </div>
            
            {debugInfo.storedDismissed && !isInstallable && (
              <div className="flex items-center justify-between rounded-lg p-3 bg-muted/50">
                <p className="text-sm text-muted-foreground">Previously dismissed installation prompt</p>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={resetInstallDismissed}
                >
                  Show Again
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  const renderNotificationSection = () => {
    return (
      <>
        <div className="space-y-2 mb-6">
          {!notificationsSupported && isInstallable && !isStandalone && (
            <div className="rounded-lg p-4 bg-amber-50 border border-amber-200 text-amber-800 space-y-3">
              <div className="flex items-start">
                <Bell className="h-5 w-5 mr-2 mt-0.5 text-amber-600" />
                <div>
                  <h4 className="font-medium">Browser Notification Limitation</h4>
                  <p className="text-sm mt-1">
                    Your browser doesn't support web push notifications. Install the mishu app to get instant alerts when your child needs attention.
                  </p>
                </div>
              </div>
              <Button 
                onClick={handleInstallClick}
                className="w-full sm:w-auto bg-mishu hover:bg-mishu-dark"
              >
                <ArrowDown className="mr-2 h-4 w-4" />
                Install for Instant Notifications
              </Button>
              <p className="text-xs text-amber-600">
                Installing takes just seconds and doesn't require an app store.
              </p>
            </div>
          )}
        </div>
        
        <NotificationSettings />
      </>
    );
  };
  
  const renderDebugSection = () => {
    return (
      <div className="border rounded-lg p-4 bg-muted/50">
        <h3 className="text-lg font-semibold mb-2">Progressive Web App (PWA) Debug</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Check the PWA installation status and detection information for troubleshooting.
        </p>
        <Button 
          variant="outline"
          onClick={() => setDebugModalOpen(true)}
        >
          Show PWA Debug Info
        </Button>
      </div>
    );
  };

  const iOSInstructionsContent = (
    <>
      {isMobile ? (
        <Sheet open={showIosInstructions} onOpenChange={setShowIosInstructions}>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>{t('pwa.iosInstallTitle', 'Install on your iOS device')}</SheetTitle>
            </SheetHeader>
            <div className="py-4 space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <MoveDown className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction1', 'Tap the Share button')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction1Detail', 'Look for the share icon in Safari')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction2', 'Tap "Add to Home Screen"')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction2Detail', 'Scroll down in the share menu if needed')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction3', 'Tap "Add" in the top right')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction3Detail', 'The app will be installed on your home screen for instant access')}</p>
                </div>
              </div>
            </div>
            <SheetFooter>
              <Button 
                onClick={() => setShowIosInstructions(false)}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {t('actions.gotIt', 'Got it')}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={showIosInstructions} onOpenChange={setShowIosInstructions}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('pwa.iosInstallTitle', 'Install on your iOS device')}</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <MoveDown className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction1', 'Tap the Share button')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction1Detail', 'Look for the share icon in Safari')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction2', 'Tap "Add to Home Screen"')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction2Detail', 'Scroll down in the share menu if needed')}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                <div className="flex-shrink-0 bg-primary text-white p-2 rounded-full">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div>
                  <p className="font-medium">{t('pwa.iosInstruction3', 'Tap "Add" in the top right')}</p>
                  <p className="text-sm text-muted-foreground">{t('pwa.iosInstruction3Detail', 'The app will be installed on your home screen for instant access')}</p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button 
                onClick={() => setShowIosInstructions(false)}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {t('actions.gotIt', 'Got it')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );

  return (
    <div className="space-y-6">
      {showOnlyInstallSection && renderInstallSection()}
      
      {showOnlyNotificationSettings && renderNotificationSection()}
      
      {!showOnlyInstallSection && !showOnlyNotificationSettings && (
        <>
          {renderInstallSection()}
          {renderNotificationSection()}
          {renderDebugSection()}
        </>
      )}
      
      <PwaDebugModal 
        open={debugModalOpen} 
        onOpenChange={setDebugModalOpen} 
        debugInfo={debugInfo}
      />

      {iOSInstructionsContent}
    </div>
  );
}
